import { forwardRef } from 'react';

import { CANCELLED, RETURNED } from 'constants/orderStatuses';
import { ORDER_PAGE_STEPS } from 'constants/orderActions';
import Tooltip from 'components/common/Tooltip';
import returnUtils from 'utils/returns';

import css from 'styles/components/account/orderTableSelectionColumn.scss';

const mixedReturnsTooltipContent = <p>Sorry, some items can't be returned together. Please submit a separate return for this item.</p>;

const exchangeStateContent = (isOrderItemsUpdating, isItemSelected, onToggleItemSelect, lineItem, orderId, testId, ref, ariaItemId) => {
  const { exchangeable, itemUniqueId } = lineItem;
  return (
    <td className={css.state}>
      <input
        type="radio"
        disabled={!exchangeable || isOrderItemsUpdating}
        name="exchange"
        id={`exchange-${itemUniqueId}`}
        checked={isItemSelected}
        onChange={() => onToggleItemSelect(lineItem, orderId, true)}
        data-test-id={testId('exchangeInput')}
        //Focus the first radio button if nothing is selected
        ref={ref}
        aria-describedby={ariaItemId}
      />
      <label htmlFor={`exchange-${itemUniqueId}`} aria-label="exchange" data-test-id={testId('exchangeCheckboxLabel')} />
    </td>
  );
};

const returnStateContent = (ariaItemId, isItemSelected, isOrderItemsUpdating, lineItem, orderId, onToggleItemSelect, selectedItems, testId) => {
  const { returnable, itemUniqueId } = lineItem;

  const isItemReturnableWithSelectedItems = isItemSelected || returnUtils.canItemBeReturnedWithSelectedItems(lineItem, selectedItems);
  const showTooltipOnHover = returnable && !isItemReturnableWithSelectedItems;
  const checkboxInput = (
    <input
      type="checkbox"
      disabled={!isItemReturnableWithSelectedItems || !returnable || isOrderItemsUpdating}
      id={`return-${itemUniqueId}`}
      checked={isItemSelected}
      onChange={() => onToggleItemSelect(lineItem, orderId, false)}
      data-test-id={testId('returnInput')}
      aria-describedby={ariaItemId}
    />
  );
  const checkboxLabel = (
    <label htmlFor={`return-${itemUniqueId}`} data-test-id={testId(`itemToReturn-${lineItem?.product?.asin}`)} aria-label="return" />
  );
  return showTooltipOnHover ? (
    <td className={css.state}>
      <div className={css.tooltipContainer}>
        <Tooltip
          wrapperClassName={css.mixedReturnsTooltipWrapper}
          contentClassName={css.mixedReturnsTooltipContent}
          tooltipClassName={css.mixedReturnsTooltip}
          mobileWrapper={css.mobileContainer}
          mobileClass={css.mobileEncompass}
          direction="right"
          content={mixedReturnsTooltipContent}
        >
          {checkboxInput}
          {checkboxLabel}
        </Tooltip>
      </div>
    </td>
  ) : (
    <td className={css.state}>
      {checkboxInput}
      {checkboxLabel}
    </td>
  );
};

const returnedStateContent = testId => (
  <td className={css.state} data-test-id={testId('returned')}>
    Returned
  </td>
);

const cancelledStateContent = testId => (
  <td className={css.state} data-test-id={testId('cancelled')}>
    Cancelled
  </td>
);

const cancelStateContent = (lineItem, onToggleItemSelect, orderId, testId, ariaItemId) => {
  const { cancellable, itemUniqueId } = lineItem;
  return (
    <td className={css.state}>
      <input
        type="checkbox"
        disabled={!cancellable}
        id={`cancel-${itemUniqueId}`}
        onChange={onToggleItemSelect.bind(this, lineItem, orderId, false)}
        data-test-id={testId('cancelInput')}
        aria-describedby={ariaItemId}
      />
      <label htmlFor={`cancel-${itemUniqueId}`} aria-label="cancel" />
    </td>
  );
};

export const SelectionColumn = forwardRef((props, ref) => {
  const { isOrderItemsUpdating, lineItem, onToggleItemSelect, orderId, selectedItems, step, testId } = props;
  const { cancellable, state, itemUniqueId } = lineItem;
  const ariaItemId = `aria-orderProductDetails-${itemUniqueId}`;
  const isItemSelected = !!selectedItems.find(selectedItem => selectedItem.itemUniqueId === itemUniqueId);

  if (step === ORDER_PAGE_STEPS.EXCHANGE_STEP) {
    return exchangeStateContent(isOrderItemsUpdating, isItemSelected, onToggleItemSelect, lineItem, orderId, testId, ref, ariaItemId);
  } else if (step === ORDER_PAGE_STEPS.RETURN_STEP) {
    return returnStateContent(ariaItemId, isItemSelected, isOrderItemsUpdating, lineItem, orderId, onToggleItemSelect, selectedItems, testId);
  } else if (state === RETURNED) {
    return returnedStateContent(testId);
  } else if (state === CANCELLED) {
    return cancelledStateContent(testId);
  } else if (cancellable) {
    return cancelStateContent(lineItem, onToggleItemSelect, orderId, testId, ariaItemId);
  }

  return <td data-test-id={testId('permanentSale')} />;
});

export default SelectionColumn;
